"use client";
import clsx from "clsx";
import type { ComponentProps, ReactNode } from "react";

import { CardListPagination } from "@widgets/card-list-pagination";
import { Section } from "@widgets/section";

import type { FilterItemType } from "@shared/content-types/types";

import type { PaginatedResponse } from "../../hooks/use-filter-pagination";
import { useFilterPaginationWithBoolean } from "../../hooks/use-filter-pagination-with-boolean";
import { Container } from "../container";
import { FiltersWithBoolean } from "../filters-with-boolean";
import { Icon } from "../icon";
import { StyledButton } from "../styled-button";

import s from "./filter-pagination-container-with-boolean.module.scss";

export interface FilterPaginationContainerWithBooleanProps<T> {
  initialContent: PaginatedResponse<T>;
  filters: FilterItemType[];
  fetchFunction: (
    filter: string,
    page: number,
  ) => Promise<PaginatedResponse<T>>;
  renderCard: (item: T, index: number) => ReactNode;
  className?: string;
  sectionClassName?: string;
  filtersClassName?: string;
  paginationClassName?: string;
  cardsClassName?: string;
  showFilters?: boolean;
  paginationThreshold?: number;
  booleanLabel?: string;
  initialBooleanFilter?: boolean;
  booleanFilterKey?: string;
  filterProps?: Partial<ComponentProps<typeof FiltersWithBoolean>>;
  mapTo?: string;
}

export const FilterPaginationContainerWithBoolean = <T,>({
  initialContent,
  filters,
  fetchFunction,
  renderCard,
  className,
  sectionClassName,
  filtersClassName,
  paginationClassName,
  cardsClassName,
  showFilters = true,
  paginationThreshold = 6,
  booleanLabel = "Только на курорте",
  initialBooleanFilter = false,
  booleanFilterKey = "onResort",
  filterProps,
  mapTo,
}: FilterPaginationContainerWithBooleanProps<T>) => {
  const {
    currentContent,
    booleanFilter,
    handleChangeFilter,
    handleBooleanFilterChange,
    handleClickOnNext,
    handleClickOnPrev,
    handleClickOnPagination,
  } = useFilterPaginationWithBoolean({
    initialContent,
    fetchFunction,
    initialBooleanFilter,
    booleanFilterKey,
  });

  return (
    <Section className={clsx(s.root, sectionClassName)}>
      <Container className={className}>
        {showFilters && (filters?.length ?? 0) > 0 && (
          <FiltersWithBoolean
            filters={filters ?? []}
            className={clsx(s.filters, filtersClassName)}
            onChangeFilter={handleChangeFilter}
            booleanFilter={booleanFilter}
            onBooleanFilterChange={handleBooleanFilterChange}
            booleanLabel={booleanLabel}
            {...filterProps}
          >
            <StyledButton
              size="s"
              href={`/map?type=resort&filters=%5B"${mapTo}"%5D`}
              aria-label="Показать на карте курорта"
            >
              <Icon className={s.mapBtnIcon} name="map" size={24} />
            </StyledButton>
          </FiltersWithBoolean>
        )}
        {currentContent?.data?.length > 0 && (
          <>
            <div className={cardsClassName}>
              {currentContent.data.map((item, index) =>
                item != null ? renderCard(item, index) : null,
              )}
            </div>
            {(currentContent?.meta?.pagination?.total ?? 0) >
              paginationThreshold && (
              <CardListPagination
                className={clsx(s.pagination, paginationClassName)}
                totalPages={currentContent?.meta?.pagination?.pageCount ?? 1}
                currentPageIndex={currentContent?.meta?.pagination?.page ?? 1}
                onNextClick={handleClickOnNext}
                onPrevClick={handleClickOnPrev}
                onPaginationClick={handleClickOnPagination}
              />
            )}
          </>
        )}
      </Container>
    </Section>
  );
};
